import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Field, Input, PasswordInput, Separator } from '@datanomik/core-ui';
import { MailLine } from '@datanomik/core-ui/icons';

import type { Credentials } from '../model/types';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface CredentialsFormProps {
  busy: boolean;
  defaultEmail?: string;
  onSubmit: (credentials: Credentials) => void;
  onForgotPassword: () => void;
  onUseSso: () => void;
}

export function CredentialsForm({
  busy,
  defaultEmail = '',
  onSubmit,
  onForgotPassword,
  onUseSso,
}: CredentialsFormProps) {
  const { t } = useTranslation();
  const { register, handleSubmit, formState } = useForm<Credentials>({
    defaultValues: { username: defaultEmail, password: '' },
  });

  const submit = handleSubmit(({ username, password }) => onSubmit({ username, password }));

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-4">
      <Field label={t('Email')} required error={formState.errors.username?.message}>
        <Input
          type="email"
          autoComplete="email"
          placeholder={t('you@company.com')}
          startAdornment={<MailLine aria-hidden="true" />}
          {...register('username', {
            required: t('Email is required'),
            pattern: { value: EMAIL_PATTERN, message: t('Please enter a valid email address') },
          })}
        />
      </Field>

      <Field label={t('Password')} required error={formState.errors.password?.message}>
        <PasswordInput
          autoComplete="current-password"
          revealLabel={t('Show password')}
          {...register('password', { required: t('Password is required') })}
        />
      </Field>

      <div className="flex justify-end">
        <Button type="button" variant="link" className="text-foreground" onClick={onForgotPassword}>
          {t('Forgot your password?')}
        </Button>
      </div>

      <Button type="submit" className="w-full" loading={busy}>
        {t('Login')}
      </Button>

      <Separator>{t('Or')}</Separator>

      <Button type="button" variant="outline" className="w-full" onClick={onUseSso}>
        {t('Login to an external account')}
      </Button>
    </form>
  );
}
